import React, { useState, useEffect } from 'react';
import { Code2, Globe, Key, Copy, CheckCircle, ChevronRight, Send, FileJson, Link2, BookOpen, Shield, Zap, Eye, EyeOff, Terminal, Hash, AlertTriangle, ArrowRight, ExternalLink, Lock, Server } from 'lucide-react';
import api from '../../services/api';

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };

const ApiManagement = () => {
  const [activeTab, setActiveTab] = useState('json');
  const [codeTab, setCodeTab] = useState('curl');
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [copied, setCopied] = useState('');
  const [loading, setLoading] = useState(true);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [configRes, profileRes] = await Promise.all([
          api.get('/user/api-config'),
          api.get('/profile'),
        ]);
        if (configRes.data?.api_base_url) setApiBaseUrl(configRes.data.api_base_url);
        if (profileRes.data?.api_key) setApiKey(profileRes.data.api_key);
      } catch (err) {
        console.error('Failed to fetch API config:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const CopyBtn = ({ text, label, size = 'sm' }) => (
    <button
      onClick={(e) => { e.stopPropagation(); copyToClipboard(text, label); }}
      className={`flex items-center gap-1 rounded-lg font-semibold transition-all hover:shadow-sm ${size === 'sm' ? 'px-2 py-1 text-[10px]' : 'px-3 py-1.5 text-xs'}`}
      style={copied === label
        ? { background: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.2)' }
        : { background: 'rgba(99, 102, 241, 0.06)', color: '#6366f1', border: '1px solid rgba(99, 102, 241, 0.12)' }
      }
    >
      {copied === label ? <><CheckCircle className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy</>}
    </button>
  );

  const maskedKey = apiKey ? apiKey.substring(0, 6) + '••••••••' + apiKey.substring(apiKey.length - 4) : '••••••••••••';
  const displayKey = showKey ? apiKey : maskedKey;

  const jsonRequestBody = JSON.stringify({
    listsms: [
      {
        sms: "Sms content will come here...",
        mobiles: "+919999999999",
        senderid: "ABCDEF",
        entityid: "1234567891112131415",
        tempid: "111111111122222"
      },
      {
        sms: "Sms content will come here...",
        mobiles: "+918888888888",
        senderid: "ABCDEF",
        entityid: "1234567891112131415",
        tempid: "1111111111111122"
      }
    ]
  }, null, 2);

  const jsonResponse = JSON.stringify({
    smslist: {
      sms: {
        reason: "success",
        code: "000",
        clientsmsid: 1947692308,
        messageid: 709462811,
        mobileno: "+919999999999",
        status: "success"
      }
    }
  }, null, 2);

  const httpResponse = JSON.stringify({
    smslist: {
      sms: {
        reason: "success",
        code: "000",
        clientsmsid: 1947692308,
        messageid: 187449454,
        mobileno: "+919999999999",
        status: "success"
      }
    }
  }, null, 2);

  const httpParams = [
    { name: 'key', value: maskedKey, desc: 'Your API authentication key', required: true },
    { name: 'mobiles', value: '+918888888888', desc: 'Customer mobile number with country code', required: true },
    { name: 'sms', value: 'test sms', desc: 'Message content (URL encoded)', required: true },
    { name: 'senderid', value: 'ABCDEF', desc: 'Approved sender ID / header', required: true },
    { name: 'entityid', value: '1234567891112131415', desc: 'DLT registered entity ID', required: true },
    { name: 'tempid', value: '1034567891112131819', desc: 'DLT registered template ID', required: true },
  ];

  const httpUrl = `${apiBaseUrl}/api/sendsms?key=${maskedKey}&mobiles=+918888888888&sms=test%20sms&senderid=ABCDEF&entityid=1234567891112131415&tempid=1034567891112131819`;

  const curlExample = `curl -X POST "${apiBaseUrl}/api/sendsms" \\
  -d "key=${apiKey || 'YOUR_API_KEY'}" \\
  -d "mobiles=+918888888888" \\
  -d "sms=test%20sms" \\
  -d "senderid=ABCDEF" \\
  -d "entityid=1234567891112131415" \\
  -d "tempid=1034567891112131819"`;

  const pythonExample = `import requests

url = "${apiBaseUrl}/api/sendsms"
params = {
    "key": "${apiKey || 'YOUR_API_KEY'}",
    "mobiles": "+918888888888",
    "sms": "test sms",
    "senderid": "ABCDEF",
    "entityid": "1234567891112131415",
    "tempid": "1034567891112131819"
}

response = requests.post(url, params=params)
print(response.json())`;

  const nodeExample = `const axios = require('axios');

const response = await axios.post('${apiBaseUrl}/api/sendsms', null, {
  params: {
    key: '${apiKey || 'YOUR_API_KEY'}',
    mobiles: '+918888888888',
    sms: 'test sms',
    senderid: 'ABCDEF',
    entityid: '1234567891112131415',
    tempid: '1034567891112131819'
  }
});

console.log(response.data);`;

  const phpExample = `<?php
$url = "${apiBaseUrl}/api/sendsms";
$params = http_build_query([
    'key'      => '${apiKey || 'YOUR_API_KEY'}',
    'mobiles'  => '+918888888888',
    'sms'      => 'test sms',
    'senderid' => 'ABCDEF',
    'entityid' => '1234567891112131415',
    'tempid'   => '1034567891112131819',
]);

$ch = curl_init("$url?$params");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
$response = curl_exec($ch);
curl_close($ch);

echo $response;`;

  const codeExamples = { curl: curlExample, python: pythonExample, nodejs: nodeExample, php: phpExample };
  const codeColors = { curl: 'text-amber-400', python: 'text-sky-400', nodejs: 'text-green-400', php: 'text-purple-400' };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Code2 className="w-5 h-5 text-white" />
            </div>
            API Documentation
          </h1>
          <p className="text-sm text-gray-500 mt-1">Integrate SMS sending into your applications using our REST API</p>
        </div>
      </div>

      {/* Credentials Section */}
      <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
        <div className="px-5 py-3.5 flex items-center gap-2" style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.06), rgba(6, 182, 212, 0.04))', borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          <Lock className="w-4 h-4 text-indigo-500" />
          <h3 className="text-sm font-bold text-gray-800">Your API Credentials</h3>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Base URL */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(99, 102, 241, 0.03)', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Server className="w-3.5 h-3.5 text-indigo-500" />
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Base URL</span>
                </div>
                <CopyBtn text={apiBaseUrl} label="baseurl" />
              </div>
              <p className="text-sm font-mono font-semibold text-gray-800 break-all">{apiBaseUrl}</p>
            </div>
            {/* API Key */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(245, 158, 11, 0.03)', border: '1px solid rgba(245, 158, 11, 0.1)' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Key className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">API Key</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setShowKey(!showKey)}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-all hover:shadow-sm"
                    style={{ background: 'rgba(99, 102, 241, 0.06)', color: '#6366f1', border: '1px solid rgba(99, 102, 241, 0.12)' }}>
                    {showKey ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    {showKey ? 'Hide' : 'Show'}
                  </button>
                  <CopyBtn text={apiKey} label="apikey" />
                </div>
              </div>
              <p className="text-sm font-mono font-semibold text-gray-800 break-all">{displayKey}</p>
            </div>
          </div>
          <div className="mt-3 flex items-start gap-2 p-3 rounded-lg" style={{ background: 'rgba(245, 158, 11, 0.05)', border: '1px solid rgba(245, 158, 11, 0.1)' }}>
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-amber-700">Keep your API key secure. Never expose it in client-side code or public repositories. You can regenerate your key from <span className="font-semibold">Settings &gt; API & Security</span>.</p>
          </div>
        </div>
      </div>

      {/* Quick Start Steps */}
      <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
        <div className="px-5 py-3.5 flex items-center gap-2" style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.06), rgba(6, 182, 212, 0.04))', borderBottom: '1px solid rgba(16, 185, 129, 0.08)' }}>
          <Zap className="w-4 h-4 text-emerald-500" />
          <h3 className="text-sm font-bold text-gray-800">Quick Start Guide</h3>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { step: '1', title: 'Get Your API Key', desc: 'Copy your API key from the credentials section above. Use it in the "key" header for all requests.', icon: Key, color: '#4f46e5' },
              { step: '2', title: 'Choose Your Method', desc: 'Use JSON API for bulk sending or HTTP API for single messages. Both support all features.', icon: Code2, color: '#059669' },
              { step: '3', title: 'Send Your First SMS', desc: 'Make a POST request with your message, mobile number, sender ID, and DLT details.', icon: Send, color: '#d97706' },
            ].map(s => (
              <div key={s.step} className="relative rounded-xl p-4" style={{ border: '1px solid rgba(99, 102, 241, 0.08)' }}>
                <div className="flex items-center gap-3 mb-2.5">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ background: s.color }}>
                    {s.step}
                  </div>
                  <h4 className="text-sm font-bold text-gray-800">{s.title}</h4>
                </div>
                <p className="text-[11px] text-gray-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* API Tabs */}
      <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
        <div className="flex gap-0 px-2 pt-2" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          {[
            { id: 'json', name: 'JSON API', icon: FileJson, badge: 'POST', badgeColor: '#4f46e5' },
            { id: 'http', name: 'HTTP API', icon: Link2, badge: 'GET/POST', badgeColor: '#059669' },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`flex items-center gap-1.5 px-5 py-2.5 text-xs font-semibold rounded-t-lg transition-colors whitespace-nowrap ${activeTab === tab.id ? 'text-indigo-600' : 'text-gray-400 hover:text-gray-600'}`}
                style={activeTab === tab.id ? { background: 'rgba(99, 102, 241, 0.06)', borderBottom: '2px solid #4f46e5' } : {}}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.name}
                <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold text-white" style={{ background: activeTab === tab.id ? tab.badgeColor : '#9ca3af' }}>{tab.badge}</span>
              </button>
            );
          })}
        </div>

        <div className="p-5">
          {activeTab === 'json' && (
            <div className="space-y-5">
              {/* Endpoint */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold text-white" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>POST</span>
                  <h3 className="text-sm font-bold text-gray-800">Send SMS via JSON</h3>
                  <span className="text-[10px] text-gray-400 font-medium ml-auto">Supports bulk sending</span>
                </div>
                <div className="flex items-center gap-2 rounded-xl p-3" style={{ background: '#1e1e2e', border: '1px solid rgba(99, 102, 241, 0.15)' }}>
                  <code className="text-xs font-mono text-emerald-400 flex-1 break-all">{apiBaseUrl}/api/json/sendsms/</code>
                  <CopyBtn text={`${apiBaseUrl}/api/json/sendsms/`} label="jsonurl" />
                </div>
              </div>

              {/* Headers */}
              <div>
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Request Headers</h4>
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(99, 102, 241, 0.08)' }}>
                  <table className="min-w-full">
                    <thead><tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Header</th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Value</th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Required</th>
                    </tr></thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                        <td className="px-4 py-2.5 text-xs font-semibold text-indigo-600 font-mono">key</td>
                        <td className="px-4 py-2.5 text-xs text-gray-600 font-mono">{maskedKey}</td>
                        <td className="px-4 py-2.5"><span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-50 text-red-600 border border-red-100">Required</span></td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2.5 text-xs font-semibold text-indigo-600 font-mono">Content-Type</td>
                        <td className="px-4 py-2.5 text-xs text-gray-600 font-mono">application/json</td>
                        <td className="px-4 py-2.5"><span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-50 text-red-600 border border-red-100">Required</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Request Body */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Request Body (JSON)</h4>
                  <CopyBtn text={jsonRequestBody} label="jsonbody" />
                </div>
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }}>
                  <div className="flex items-center justify-between px-4 py-2" style={{ background: '#252535' }}>
                    <span className="text-[10px] font-mono text-gray-400">application/json</span>
                  </div>
                  <div className="p-4 overflow-x-auto" style={{ background: '#1e1e2e' }}>
                    <pre className="text-xs font-mono text-emerald-400 whitespace-pre">{jsonRequestBody}</pre>
                  </div>
                </div>
              </div>

              {/* Body Fields */}
              <div>
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Body Fields (per SMS object in <code className="text-indigo-500">listsms</code> array)</h4>
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(99, 102, 241, 0.08)' }}>
                  <table className="min-w-full">
                    <thead><tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Field</th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Required</th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Description</th>
                    </tr></thead>
                    <tbody>
                      {[
                        { field: 'sms', type: 'string', desc: 'Message content (DLT approved template text)', required: true },
                        { field: 'mobiles', type: 'string', desc: 'Recipient mobile with country code (+91...)', required: true },
                        { field: 'senderid', type: 'string', desc: 'DLT approved sender ID / header (6 chars)', required: true },
                        { field: 'entityid', type: 'string', desc: 'DLT registered principal entity ID', required: true },
                        { field: 'tempid', type: 'string', desc: 'DLT registered content template ID', required: true },
                      ].map((f) => (
                        <tr key={f.field} style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                          <td className="px-4 py-2.5 text-xs font-semibold text-indigo-600 font-mono">{f.field}</td>
                          <td className="px-4 py-2.5"><span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5' }}>{f.type}</span></td>
                          <td className="px-4 py-2.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${f.required ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>{f.required ? 'Yes' : 'No'}</span></td>
                          <td className="px-4 py-2.5 text-xs text-gray-600">{f.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Response */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Response</h4>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-50 text-green-600 border border-green-100">200 OK</span>
                  </div>
                  <CopyBtn text={jsonResponse} label="jsonresp" />
                </div>
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(16, 185, 129, 0.15)' }}>
                  <div className="flex items-center px-4 py-2" style={{ background: '#252535' }}>
                    <span className="text-[10px] font-mono text-green-400">Success Response</span>
                  </div>
                  <div className="p-4 overflow-x-auto" style={{ background: '#1e1e2e' }}>
                    <pre className="text-xs font-mono text-cyan-400 whitespace-pre">{jsonResponse}</pre>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'http' && (
            <div className="space-y-5">
              {/* Endpoint */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold text-white" style={{ background: 'linear-gradient(135deg, #059669, #06b6d4)' }}>GET / POST</span>
                  <h3 className="text-sm font-bold text-gray-800">Send SMS via HTTP Query</h3>
                  <span className="text-[10px] text-gray-400 font-medium ml-auto">Single message</span>
                </div>
                <div className="flex items-center gap-2 rounded-xl p-3" style={{ background: '#1e1e2e', border: '1px solid rgba(99, 102, 241, 0.15)' }}>
                  <code className="text-xs font-mono text-emerald-400 flex-1 break-all">{apiBaseUrl}/api/sendsms?key=...&mobiles=...&sms=...</code>
                  <CopyBtn text={httpUrl} label="httpurl" />
                </div>
              </div>

              {/* Parameters Table */}
              <div>
                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Query Parameters</h4>
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(99, 102, 241, 0.08)' }}>
                  <table className="min-w-full">
                    <thead><tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Parameter</th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Example</th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Required</th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Description</th>
                    </tr></thead>
                    <tbody>
                      {httpParams.map((p) => (
                        <tr key={p.name} style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                          <td className="px-4 py-2.5 text-xs font-semibold text-indigo-600 font-mono">{p.name}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-600 font-mono">{p.value}</td>
                          <td className="px-4 py-2.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${p.required ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>{p.required ? 'Yes' : 'No'}</span></td>
                          <td className="px-4 py-2.5 text-xs text-gray-600">{p.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Response */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Response</h4>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-50 text-green-600 border border-green-100">200 OK</span>
                  </div>
                  <CopyBtn text={httpResponse} label="httpresp" />
                </div>
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(16, 185, 129, 0.15)' }}>
                  <div className="flex items-center px-4 py-2" style={{ background: '#252535' }}>
                    <span className="text-[10px] font-mono text-green-400">Success Response</span>
                  </div>
                  <div className="p-4 overflow-x-auto" style={{ background: '#1e1e2e' }}>
                    <pre className="text-xs font-mono text-cyan-400 whitespace-pre">{httpResponse}</pre>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Code Examples */}
      <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
        <div className="px-5 py-3.5 flex items-center gap-2" style={{ background: 'rgba(99, 102, 241, 0.04)', borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          <Terminal className="w-4 h-4 text-indigo-500" />
          <h3 className="text-sm font-bold text-gray-800">Code Examples</h3>
        </div>
        <div>
          <div className="flex gap-0 px-3 pt-2" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
            {[
              { id: 'curl', name: 'cURL' },
              { id: 'python', name: 'Python' },
              { id: 'nodejs', name: 'Node.js' },
              { id: 'php', name: 'PHP' },
            ].map(t => (
              <button key={t.id} onClick={() => setCodeTab(t.id)}
                className={`px-4 py-2 text-xs font-semibold rounded-t-lg transition-colors ${codeTab === t.id ? 'text-indigo-600' : 'text-gray-400 hover:text-gray-600'}`}
                style={codeTab === t.id ? { background: 'rgba(99, 102, 241, 0.06)', borderBottom: '2px solid #4f46e5' } : {}}>
                {t.name}
              </button>
            ))}
          </div>
          <div className="relative">
            <div className="absolute top-3 right-3 z-10">
              <CopyBtn text={codeExamples[codeTab]} label={`code-${codeTab}`} />
            </div>
            <div className="p-4 overflow-x-auto" style={{ background: '#1e1e2e' }}>
              <pre className={`text-xs font-mono ${codeColors[codeTab]} whitespace-pre-wrap`}>{codeExamples[codeTab]}</pre>
            </div>
          </div>
        </div>
      </div>

      {/* Response Codes */}
      <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
        <div className="px-5 py-3.5 flex items-center gap-2" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          <Hash className="w-4 h-4 text-indigo-500" />
          <h3 className="text-sm font-bold text-gray-800">Response Codes</h3>
        </div>
        <div className="p-5">
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(99, 102, 241, 0.08)' }}>
            <table className="min-w-full">
              <thead><tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Description</th>
              </tr></thead>
              <tbody>
                {[
                  { code: '000', status: 'Success', desc: 'Message accepted for delivery', color: '#059669' },
                  { code: '001', status: 'Invalid Key', desc: 'API key is missing or invalid', color: '#dc2626' },
                  { code: '002', status: 'Invalid Mobile', desc: 'Mobile number format is incorrect', color: '#dc2626' },
                  { code: '003', status: 'Insufficient Balance', desc: 'Account balance too low to send', color: '#d97706' },
                  { code: '004', status: 'DLT Validation Failed', desc: 'Entity ID or Template ID not registered', color: '#dc2626' },
                  { code: '005', status: 'Rate Limited', desc: 'TPS limit exceeded, retry after cooldown', color: '#d97706' },
                ].map((r) => (
                  <tr key={r.code} style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                    <td className="px-4 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: `${r.color}15`, color: r.color, border: `1px solid ${r.color}30` }}>{r.code}</span></td>
                    <td className="px-4 py-2.5 text-xs font-semibold text-gray-800">{r.status}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-600">{r.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Quick Tips */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl p-4" style={cardStyle}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(99, 102, 241, 0.1)' }}>
              <Zap className="w-4 h-4" style={{ color: '#4f46e5' }} />
            </div>
            <div>
              <h4 className="text-xs font-bold text-gray-800 mb-1">Bulk Sending</h4>
              <p className="text-[11px] text-gray-500 leading-relaxed">Send up to 100 messages in one request using the JSON API <code className="px-1 py-0.5 rounded bg-indigo-50 text-indigo-600 text-[10px] font-mono">listsms</code> array.</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4" style={cardStyle}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
              <Shield className="w-4 h-4" style={{ color: '#059669' }} />
            </div>
            <div>
              <h4 className="text-xs font-bold text-gray-800 mb-1">Security Best Practices</h4>
              <p className="text-[11px] text-gray-500 leading-relaxed">Always call from server-side. Use IP whitelisting for extra security. Rotate keys periodically.</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4" style={cardStyle}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(245, 158, 11, 0.1)' }}>
              <Globe className="w-4 h-4" style={{ color: '#d97706' }} />
            </div>
            <div>
              <h4 className="text-xs font-bold text-gray-800 mb-1">DLT Compliance</h4>
              <p className="text-[11px] text-gray-500 leading-relaxed">All messages require valid DLT entity ID, template ID, and approved sender ID as per TRAI regulations.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiManagement;
